import React from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Star, Sparkles, Crown } from 'lucide-react';
import { AnimatedSection } from './ui/AnimatedSection';
import { trackInitiateCheckout } from './MetaPixel';
// Pack definitions used for InitiateCheckout event payloads
const PACKS = {
  pro: {
    id: 'mega-pack-kinesiologia',
    name: 'Mega Pack Kinesiología',
    value: 24990
  },
  experto: {
    id: 'mega-pack-kinesiologia-guias',
    name: 'Mega Pack Kinesiología + Guías Prácticas de Ejercicios',
    value: 34990
  }
};
export function PricingOptions() {
  return <AnimatedSection id="pricing" className="bg-navy-950 relative overflow-hidden" data-id="element-274">
      {/* Subtle dot pattern */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" data-id="element-275">
        <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(circle, rgba(201,168,76,0.4) 1px, transparent 1px)',
        backgroundSize: '40px 40px'
      }} data-id="element-276" />
      </div>

      {/* Ambient glows */}
      <div className="absolute top-1/4 -left-40 w-[500px] h-[500px] bg-gold/[0.05] blur-[120px] rounded-full pointer-events-none" data-id="element-277"></div>
      <div className="absolute bottom-1/4 -right-40 w-[500px] h-[500px] bg-emerald-500/[0.06] blur-[120px] rounded-full pointer-events-none" data-id="element-278"></div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-279">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-14" data-id="element-280">
          <div className="inline-block px-3 py-1 rounded-full border border-gold/30 bg-gold/5 text-gold text-[10px] sm:text-xs font-bold tracking-[0.15em] uppercase mb-4 sm:mb-5" data-id="element-281">
            Tu inversión
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-6xl font-heading font-extrabold text-white leading-[1.05] mb-3" data-id="element-282">
            Elegí tu{' '}
            <span className="font-editorial italic text-gold font-medium" data-id="element-283">
              Mega Pack
            </span>
          </h2>
          <p className="text-sm sm:text-base text-gray-400" data-id="element-284">
            Dos formas de entrar al sistema. Una sola decisión.
          </p>
        </div>

        {/* Stacked list of cards */}
        <div className="flex flex-col gap-7 sm:gap-8 max-w-2xl mx-auto pt-5 sm:pt-8" data-id="element-285">
          {/* ─────────────────────────────────────────────
           OPCIÓN 1 — MEGA PACK KINESIOLOGÍA · MÁS ELEGIDA
           ──────────────────────────────────────────────*/}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.08
        }} className="relative bg-gradient-to-b from-navy-900 to-navy-950 border-2 border-emerald-500 rounded-3xl p-5 sm:p-7 flex flex-col shadow-[0_0_60px_rgba(16,185,129,0.28)]" data-id="element-286">
            {/* Glow ring on top */}
            <div className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-emerald-400 to-transparent" data-id="element-287" />

            {/* Pill */}
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10" data-id="element-288">
              <div className="inline-flex items-center gap-1.5 bg-emerald-500 text-white font-bold px-4 py-1.5 rounded-full text-[10px] sm:text-xs tracking-[0.15em] uppercase shadow-[0_4px_20px_rgba(16,185,129,0.5)]" data-id="element-289">
                <Star size={11} className="fill-white" data-id="element-290" />
                MÁS ELEGIDA
              </div>
            </div>

            {/* Title */}
            <div className="mb-4 mt-2" data-id="element-291">
              <h3 className="text-base sm:text-lg font-heading font-bold text-white mb-1.5 leading-tight" data-id="element-292">
                MEGA PACK KINESIOLOGÍA
              </h3>
              <p className="text-emerald-300/90 text-[13px] sm:text-sm leading-snug" data-id="element-293">
                La opción ideal para dominar la evaluación y la toma de
                decisiones clínicas.
              </p>
            </div>

            {/* Price */}
            <div className="mb-5 pb-5 border-b border-emerald-500/15" data-id="element-294">
              <div className="text-red-400/70 line-through text-sm sm:text-base mb-1" data-id="element-295">
                $59.000
              </div>
              <div className="flex items-baseline gap-2" data-id="element-296">
                <span className="text-3xl sm:text-[2.5rem] font-heading font-extrabold text-white tracking-tight leading-none" data-id="element-297">
                  $24.990
                </span>
                <span className="text-[10px] sm:text-xs text-gray-500 font-semibold tracking-widest uppercase" data-id="element-298">
                  ARS
                </span>
              </div>
            </div>

            {/* Bullets */}
            <ul className="space-y-3 mb-6 flex-1" data-id="element-299">
              {[{
              text: 'Todo lo del Mega Pack Inicial',
              highlight: false
            }, {
              text: 'Razonamiento clínico',
              highlight: true
            }, {
              text: 'Casos y resolución aplicada',
              highlight: false
            }].map((feat, idx) => <li key={idx} className={`flex items-start gap-3 text-[13px] sm:text-sm ${feat.highlight ? 'bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 -mx-1 text-white font-semibold' : 'text-gray-200'}`} data-id="element-300">
                  <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${feat.highlight ? 'bg-emerald-500 border border-emerald-400' : 'bg-emerald-500/15 border border-emerald-500/30'}`} data-id="element-301">
                    <Check size={11} className={feat.highlight ? 'text-white' : 'text-emerald-400'} strokeWidth={3} data-id="element-302" />
                  </div>
                  <span data-id="element-303">{feat.text}</span>
                </li>)}
            </ul>

            {/* CTA */}
            <a href="https://checkout.atlasclinico.com/cart/45616790143078:1" target="_top" rel="noopener" onClick={() => trackInitiateCheckout(PACKS.pro)} className="group block w-full text-center bg-emerald-500 hover:bg-emerald-400 text-navy-950 font-extrabold text-sm sm:text-base px-5 py-3.5 rounded-2xl transition-all transform hover:scale-[1.02] shadow-[0_10px_30px_rgba(16,185,129,0.35)]" data-id="element-304">
              <span className="inline-flex items-center gap-2" data-id="element-305">
                QUIERO EL PACK + AVANZADO
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-306" />
              </span>
            </a>
          </motion.div>

          {/* ─────────────────────────────────────────────
           OPCIÓN 2 — MEGA PACK + GUÍAS PRÁCTICAS · MÁS COMPLETO
           ──────────────────────────────────────────────*/}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.16
        }} className="relative bg-gradient-to-b from-[#1a1a2e] via-navy-900 to-navy-950 border border-gold/50 rounded-3xl p-5 sm:p-6 flex flex-col shadow-[0_0_40px_rgba(201,168,76,0.18)]" data-id="element-307">
            {/* Top gold accent line */}
            <div className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-gold to-transparent" data-id="element-308" />

            {/* Pill — subtle premium */}
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10" data-id="element-309">
              <div className="inline-flex items-center gap-1.5 bg-gradient-to-r from-gold to-gold-light text-navy-950 font-bold px-4 py-1.5 rounded-full text-[10px] sm:text-xs tracking-[0.15em] uppercase shadow-[0_4px_20px_rgba(201,168,76,0.45)]" data-id="element-310">
                <Crown size={11} className="fill-navy-950" data-id="element-311" />
                MÁS COMPLETO
              </div>
            </div>

            {/* Title */}
            <div className="mb-4 mt-2" data-id="element-312">
              <h3 className="text-base sm:text-lg font-heading font-bold text-white mb-1.5 leading-tight" data-id="element-313">
                MEGA PACK KINESIOLOGÍA + GUÍAS PRÁCTICAS DE EJERCICIOS
              </h3>
              <p className="text-gold/80 text-[13px] sm:text-sm leading-snug" data-id="element-314">
                Todo el sistema clínico más la herramienta de ejercicio
                terapéutico para aplicar con pacientes reales.
              </p>
            </div>

            {/* Price */}
            <div className="mb-5 pb-5 border-b border-gold/15" data-id="element-315">
              <div className="text-red-400/70 line-through text-sm sm:text-base mb-1" data-id="element-316">
                $79.000
              </div>
              <div className="flex items-baseline gap-2" data-id="element-317">
                <span className="text-3xl sm:text-4xl font-heading font-extrabold text-white tracking-tight" data-id="element-318">
                  $34.990
                </span>
                <span className="text-[10px] sm:text-xs text-gray-500 font-semibold tracking-widest uppercase" data-id="element-319">
                  ARS
                </span>
              </div>
            </div>

            {/* Bullets */}
            <ul className="space-y-3 mb-6 flex-1" data-id="element-320">
              {/* Simple bullets */}
              {['Todo el contenido del Mega Pack Kinesiología', 'Protocolos de progresión', 'Material clínico complementario'].map((feat, idx) => <li key={idx} className="flex items-start gap-3 text-[13px] sm:text-sm text-gray-200" data-id="element-321">
                  <div className="mt-0.5 w-5 h-5 rounded-full bg-gradient-to-br from-gold to-gold-hover border border-gold flex items-center justify-center flex-shrink-0 shadow-[0_0_8px_rgba(201,168,76,0.4)]" data-id="element-322">
                    <Check size={11} className="text-navy-950" strokeWidth={3} data-id="element-323" />
                  </div>
                  <span data-id="element-324">{feat}</span>
                </li>)}

              {/* Highlighted feature — Sistema Clínico de Ejercicio Terapéutico */}
              <li className="flex items-start gap-3 bg-gold/10 border border-gold/30 rounded-lg px-3 py-2.5 -mx-1" data-id="element-325">
                <div className="mt-0.5 w-5 h-5 rounded-full bg-gradient-to-br from-gold to-gold-hover border border-gold flex items-center justify-center flex-shrink-0 shadow-[0_0_8px_rgba(201,168,76,0.4)]" data-id="element-326">
                  <Check size={11} className="text-navy-950" strokeWidth={3} data-id="element-327" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-328">
                  <p className="text-[13px] sm:text-sm text-white font-semibold leading-snug" data-id="element-329">
                    Sistema Clínico de Ejercicio Terapéutico
                  </p>
                  <p className="text-[12px] sm:text-[13px] text-gold/90 leading-snug mt-0.5" data-id="element-330">
                    40 protocolos progresivos para aplicar en pacientes reales.
                  </p>
                  <p className="text-[11px] sm:text-xs text-gray-400 leading-snug mt-1" data-id="element-331">
                    Incluye ejercicios de movilidad, fortalecimiento, control
                    motor y progresiones terapéuticas.
                  </p>
                </div>
              </li>
            </ul>

            {/* CTA */}
            <a href="https://checkout.atlasclinico.com/cart/45675793186918:1" target="_top" rel="noopener" onClick={() => trackInitiateCheckout(PACKS.experto)} className="group block w-full text-center bg-gradient-to-r from-gold to-gold-hover hover:from-gold-light hover:to-gold text-navy-950 font-extrabold text-[13px] sm:text-sm px-5 py-3.5 rounded-2xl transition-all transform hover:scale-[1.02] shadow-[0_10px_30px_rgba(201,168,76,0.35)]" data-id="element-332">
              <span className="inline-flex items-center gap-2" data-id="element-333">
                QUIERO EL PACK EXPERTO
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" data-id="element-334" />
              </span>
            </a>
          </motion.div>
        </div>

        {/* Trust strip below */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.3
      }} className="flex flex-wrap justify-center items-center gap-x-4 sm:gap-x-6 gap-y-2 mt-10 sm:mt-14 text-xs sm:text-sm text-gray-400" data-id="element-335">
          <div className="flex items-center gap-1.5" data-id="element-336">
            🛡️ <span data-id="element-337">Garantía 7 días</span>
          </div>
          <span className="text-gray-600" data-id="element-338">·</span>
          <div className="flex items-center gap-1.5" data-id="element-339">
            🔒 <span data-id="element-340">Pago seguro</span>
          </div>
          <span className="text-gray-600" data-id="element-341">·</span>
          <div className="flex items-center gap-1.5" data-id="element-342">
            💳 <span data-id="element-343">Mercado Pago · Tarjeta · Transferencia</span>
          </div>
        </motion.div>
      </div>
    </AnimatedSection>;
}